import {StorageConditionsContentMap, storageConditionsFields} from '@/modules/drug-catalog/stability-rules/constants'
import {useStabilityRuleStorageLabel} from '@/modules/drug-catalog/stability-rules/functions'
import {StabilityRuleFormSchema} from '@/modules/drug-catalog/stability-rules/schemas'
import {DataList} from '@cypro/ui/components/ui/data-list'
import {getFirstElementOrSelf} from '@cypro/ui/utils'
import {Badge, Box, CheckboxCard, CheckboxCardRootProps, Heading, HStack, IconButton, Menu} from '@chakra-ui/react'
import {StabilityRule} from '@cypro/engine/schema'
import {useTranslate} from '@tolgee/react'
import {PropsWithChildren} from 'react'
import {LuEllipsisVertical, LuPen, LuTrash2} from 'react-icons/lu'

interface Props {
  rule: StabilityRuleFormSchema | StabilityRule
  onDelete?: VoidFunction
  onEdit?: VoidFunction
}

export function StabilityRuleCard({rule, onDelete, onEdit}: Props) {
  const {t} = useTranslate('common')

  return (
    <Box padding="5" borderWidth="1px" borderStyle="solid" borderColor="border" borderRadius="l3" background="bg">
      <Content rule={rule}>
        {(onEdit || onDelete) && (
          <Menu.Root>
            <Menu.Trigger asChild>
              <IconButton size="sm" variant="ghost" title={t('more_actions')}>
                <LuEllipsisVertical />
              </IconButton>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content>
                {onEdit && (
                  <Menu.Item value="edit" onSelect={onEdit}>
                    <LuPen />
                    {t('edit')}
                  </Menu.Item>
                )}
                {onDelete && (
                  <Menu.Item value="delete" onSelect={onDelete}>
                    <LuTrash2 />
                    {t('delete')}
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        )}
      </Content>
    </Box>
  )
}

interface StabilityRuleCheckboxCardProps extends CheckboxCardRootProps {
  rule: StabilityRuleFormSchema | StabilityRule
}

export function StabilityRuleCheckboxCard({rule, ...props}: StabilityRuleCheckboxCardProps) {
  return (
    <CheckboxCard.Root {...props}>
      <CheckboxCard.HiddenInput />

      <CheckboxCard.Control>
        <Content rule={rule}>
          <CheckboxCard.Indicator />
        </Content>
      </CheckboxCard.Control>
    </CheckboxCard.Root>
  )
}

interface ContentProps extends PropsWithChildren {
  rule: StabilityRuleFormSchema | StabilityRule
}

function Content({rule, children}: ContentProps) {
  const {t} = useTranslate('common')

  let defaultRule = false
  if ('defaultRule' in rule) defaultRule = Boolean(rule.defaultRule)
  else if ('isDefaultStage' in rule) defaultRule = Boolean(rule.isDefaultStage)

  const storageLabel = useStabilityRuleStorageLabel(rule)

  return (
    <Box width="100%" spaceY="4">
      <HStack width="100%" spaceX="2" justifyContent="space-between">
        <HStack>
          <Heading as="h3" fontSize="sm">
            {t(getFirstElementOrSelf(rule.stabilityStage))}
          </Heading>
          {defaultRule && (
            <Badge size="sm" colorPalette="cyan">
              {t('default')}
            </Badge>
          )}
        </HStack>

        {children}
      </HStack>

      <DataList
        items={[
          {label: t('condition'), value: storageLabel},
          {
            label: t('time'),
            value: [
              rule.durationValue,
              t(getFirstElementOrSelf(rule.durationUnit)),
              `(${t('from_x', {value: t(getFirstElementOrSelf(rule.stabilityStage))})})`,
            ].join(' '),
          },
        ]}
      />

      {storageConditionsFields.length > 0 && (
        <HStack flexWrap="wrap" width="100%">
          {storageConditionsFields.map(cond => {
            if (!rule[cond]) return null

            return (
              <Badge key={cond}>
                {StorageConditionsContentMap[cond].icon({size: 12})}
                {t(StorageConditionsContentMap[cond].label)}
              </Badge>
            )
          })}
        </HStack>
      )}
    </Box>
  )
}
